import Seo from "@/components/Seo";
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { linkify } from "@/lib/linkify";
import { freshnessLabel } from "@/lib/time";

export default function DataItemDetail() {
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    api.get(`/data-hub/${id}`)
      .then((r) => { setItem(r.data); setStatus("ok"); })
      .catch(() => setStatus("notfound"));
  }, [id]);

  if (status === "loading") {
    return <div className="max-w-3xl mx-auto px-6 py-24 text-[#94A3B8]" data-testid="data-detail-loading">Loading…</div>;
  }
  if (status === "notfound" || !item) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="data-detail-notfound">
        <p className="text-[#94A3B8]">This data item could not be found.</p>
        <Link to="/ai-progress" className="text-[#00F0FF] mt-4 inline-block">← Back to AI Data &amp; Statistics</Link>
      </div>
    );
  }

  return (
    <article className="max-w-3xl mx-auto px-6 md:px-8 py-10 md:py-14" data-testid="data-detail-page">
      <Seo
        title={`${item.title} — AI Data & Statistics | WorldAIInfo`}
        description={(item.description || item.title).slice(0, 300)}
        image={item.image_url}
        canonicalPath={`/data/${id}`}
        type="article"
      />
      <Link to="/ai-progress" className="inline-flex items-center gap-1.5 text-sm text-[#94A3B8] hover:text-[#00F0FF] transition mb-8" data-testid="data-detail-back">
        <ArrowLeft className="w-4 h-4" /> Back to AI Data &amp; Statistics
      </Link>

      <h1 className="font-display font-extrabold text-3xl md:text-4xl leading-tight" data-testid="data-detail-title">{item.title}</h1>

      {freshnessLabel(item) && (
        <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-mono-tech uppercase text-[#00F0FF]" data-testid="data-detail-freshness">
          <Clock className="w-3.5 h-3.5" /> {freshnessLabel(item)}
        </div>
      )}

      {item.type === "image" && item.image_url && (
        <a href={item.image_url} target="_blank" rel="noopener noreferrer"
          className="block mt-8 rounded-2xl overflow-hidden border border-white/10 bg-[#0B0F19]"
          title="View full-size chart" data-testid="data-detail-image-link">
          <img src={item.image_url} alt={item.title} className="w-full object-contain" />
        </a>
      )}

      {item.description && (
        <div className="mt-8 text-[#CBD5E1] text-base md:text-lg leading-relaxed whitespace-pre-line break-words" data-testid="data-detail-description">
          {linkify(item.description)}
        </div>
      )}

      {item.source_name && (
        <div className="mt-12 pt-6 border-t border-white/10" data-testid="data-detail-citation">
          <h2 className="font-mono-tech text-xs uppercase text-[#64748B] mb-2 tracking-wider">Source</h2>
          <a href={item.source_url || "#"} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-[#00F0FF] hover:underline break-words" data-testid="data-detail-source">
            [1] {item.source_name}{item.source_date ? `, ${item.source_date}` : ""} <ExternalLink className="w-3.5 h-3.5 shrink-0" />
          </a>
        </div>
      )}
    </article>
  );
}
